/**
 * The published selection, as a state a screen can render.
 *
 * The same query the ranking panel uses, under the same key, so opening this
 * screen costs no second request and the two can never show windows from two
 * different passes.
 *
 * It reads its own copy rather than importing the ranking feature's hook: one
 * feature reaching into another's internals is what the architecture test
 * refuses, and rightly — the shape below is this screen's to change.
 */
import { useQuery } from "@tanstack/react-query";

import { useSession } from "@/features/auth";
import type { RankingResponse } from "@/shared/api";
import { rankingKey, readCurrentRanking } from "@/shared/ranking/readCurrentRanking";
import { statusOf } from "@/shared/http/errors";

export type ScannerState =
  | { readonly status: "loading" }
  | { readonly status: "forbidden" }
  | { readonly status: "unreadable" }
  | { readonly status: "ready"; readonly ranking: RankingResponse };

export function useScanner(): ScannerState {
  const platform = useSession().status === "platform";
  const query = useQuery({
    queryKey: rankingKey(platform),
    queryFn: () => readCurrentRanking(platform),
    // A pass runs on a quarter-hour timer, so asking more often re-reads rows
    // that have not moved. A minute keeps the age on screen honest.
    refetchInterval: 60_000,
    retry: false,
  });

  if (query.isPending) return { status: "loading" };
  if (query.isError) {
    return statusOf(query.error) === 403
      ? { status: "forbidden" }
      : { status: "unreadable" };
  }
  return { status: "ready", ranking: query.data };
}
